import type { ID, PoiCreateForm, PoiUpdateForm } from "@situm/sdk-js";
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import distance from "@turf/distance";
import { polygon, point as turfPoint } from "@turf/helpers";
import pointToPolygonDistance from "@turf/point-to-polygon-distance";
import type { Feature, FeatureCollection, Point } from "geojson";
import { decode } from "he";
import { t } from "i18next";
import { isEqual } from "lodash-es";
import type { IconType } from "react-icons";

import { CDN_SERVER_DOMAIN } from "@/constants";
import { CarIcon } from "@/features/app/components/icons";
import {
  type GenericOverlayFeature,
  type OverlayFeatureCollection,
  OverlayFeatureTypes,
  type PoiOverlayFeature,
} from "@/features/editor/types";
import { createPoiOverlayFeatureId } from "@/features/editor/utils/idGenerator";
import type { SliceProps } from "@/features/find_my_car/types";
import { MAX_ZOOM, MIN_ZOOM } from "@/features/map/constants";
import {
  getIcon,
  getPoiVisibility,
  getSelectedIcon,
} from "@/features/map/layers/Poi/utils";
import { LocationStatusTypes } from "@/features/utils/types";
import { containsKnownTag } from "@/features/utils/utils";
import type IMDF from "@/libs/imdf/imdf";
import { Geofence, Geofences } from "@/libs/models/geofence";
import type { PathLink, Paths } from "@/libs/models/path";
import i18n from "../app/i18n";
import CoordinateConverter, { CoordinateConverterFlatEarth } from "./geo";

export const CURRENT_USER_LOCATION_ID = -1;
export const FAV_POI_CATEGORY_ID = -1;
export const CUSTOM_DESTINATION_LOCATION_ID = -2;
export const FIND_MY_CAR_LOCATION_ID = -3;
export const SHARED_LOCATION_ID = -4;

export const CUSTOM_POI_IDS = new Set<number>([
  CURRENT_USER_LOCATION_ID,
  FAV_POI_CATEGORY_ID,
  CUSTOM_DESTINATION_LOCATION_ID,
  FIND_MY_CAR_LOCATION_ID,
  SHARED_LOCATION_ID,
]);

export const DEFAULT_POI_ICON_PATH = "images/default.png";
export const DEFAULT_POI_ICON_FILENAME = "default.png";
export const EXTERNAL_ID_CUSTOM_FIELD = "external_id";

const ensureAbsoluteCdnUrl = (url: string | null): string | null => {
  if (!url || !url.includes("/uploads")) return url;
  const hasHttps = url.startsWith("https://");
  if (hasHttps) {
    const modifiedURL = new URL(url);
    modifiedURL.hostname = CDN_SERVER_DOMAIN;
    return modifiedURL.href;
  } else {
    return `https://${CDN_SERVER_DOMAIN}${url}`;
  }
};

export interface CustomField {
  key: string;
  value: string;
}

export class CustomFields<T> {
  constructor(public cf: CustomField[]) {}

  getValueOfKey(key: T) {
    return this.cf.find((e) => e.key == key)?.value || null;
  }

  toArray() {
    return this.cf;
  }

  isEqual(otherInstance: this): boolean {
    return isEqual(this, otherInstance);
  }

  static fromMap(data: any[]) {
    return new CustomFields(
      Object.values(data)?.map((e) => {
        return { key: e.key, value: e.value };
      }),
    );
  }
}

export class BuildingGeometry {
  constructor(
    public location: LatLng,
    public corners: Corners,
    public width: number,
    public length: number,
    public rotation: number,
  ) {
    this.location = location;
    this.corners = corners;
    this.width = width;
    this.length = length;
    this.rotation = rotation;
  }

  static fromMap(data: any) {
    return new BuildingGeometry(
      LatLng.fromMap(data.location),
      Corners.fromMap(data.corners),
      data.width,
      data.length,
      data.rotation,
    );
  }
}

export class Building {
  public coordinateConverter: CoordinateConverter;
  public coordinateConverterFlatEarth: CoordinateConverterFlatEarth;

  constructor(
    public id: ID,
    public name: string,
    public address: string,
    public info: string,
    public pictureUrl: string,
    public geometry: BuildingGeometry,
    public customFields: CustomFields<string>,
    public floors?: Floor[],
    public pois?: Pois,
    public paths?: Paths,
    public geofences?: Geofences,
    public geojson?: IMDF | null,
    public pathTags?: string[],
    public overlays?: Record<string, GenericOverlayFeature>,
  ) {
    this.floors = floors?.sort((a, b) => {
      // sort floors by level
      if (a.level > b.level) {
        return -1;
      }
      if (a.level < b.level) {
        return 1;
      }

      return 0;
    });

    this.coordinateConverter = new CoordinateConverter(
      this.geometry.location,
      this.geometry.width,
      this.geometry.length,
      this.geometry.rotation,
    );

    this.coordinateConverterFlatEarth = new CoordinateConverterFlatEarth(
      this.geometry.corners.bottomRight,
      this.geometry.corners.bottomLeft,
      this.geometry.corners.topLeft,
      this.geometry.corners.topRight,
      this.geometry.width,
      this.geometry.length,
    );

    this.pictureUrl = ensureAbsoluteCdnUrl(this.pictureUrl);

    // Always create an empty dict for overlays even when no parameter is set
    this.overlays = overlays ?? {};
  }

  copy(overrides: Partial<Building> = {}): Building {
    return new Building(
      overrides.id || this.id,
      overrides.name || this.name,
      overrides.address || this.address,
      overrides.info || this.info,
      overrides.pictureUrl || this.pictureUrl,
      overrides.geometry || this.geometry,
      overrides.customFields || this.customFields,
      overrides.floors || this.floors,
      overrides.pois || this.pois,
      overrides.paths || this.paths,
      overrides.geofences || this.geofences,
      overrides.geojson || this.geojson,
      overrides.pathTags || this.pathTags,
      overrides.overlays || this.overlays,
    );
  }

  getFloorByLevel(level: number) {
    return this.floors?.find((e) => e.level == level);
  }

  getFloorById(floor_id: ID) {
    return this.floors?.find((e) => e.id == floor_id);
  }

  getFirstFloor() {
    return this.getFloorByLevel(0) || this.floors?.at(-1);
  }

  getNextFloor(floorId: number, ascendent: boolean = true): Floor | null {
    if (!this.floors) return null;

    const sortedFloors = [...this.floors].sort((a, b) =>
      ascendent ? a.level - b.level : b.level - a.level,
    );
    const currentFloorIndex = sortedFloors.findIndex(
      (floor) => floor.id === floorId,
    );

    if (
      currentFloorIndex === -1 ||
      currentFloorIndex === sortedFloors.length - 1
    )
      return null;

    return sortedFloors[currentFloorIndex + 1];
  }

  getPaths() {
    return this.paths;
  }

  getPathsByFloorId(floorId: ID): Paths {
    if (!floorId || !this.paths) {
      return { links: [], nodes: [] } as Paths;
    }

    const nodes = this.paths?.nodes.filter((n) => n.floorId == floorId);
    const nodeInFloorIds = nodes.map((n) => n.id);

    const links = this.paths?.links.filter(
      (l: PathLink) =>
        nodeInFloorIds.includes(l.source) || nodeInFloorIds.includes(l.target),
    );
    return { links, nodes } as Paths;
  }

  getCorners(): Corners {
    return this.geometry.corners || null;
  }

  getBounds(): LatLng[] {
    return this.geometry.corners.toArray() || [];
  }
  getArea(): number {
    return this.geometry.width * this.geometry.width;
  }

  getLocation(): LatLng {
    return this.geometry.location;
  }

  getCustomFieldValue(name: string): string | null {
    return this.customFields.getValueOfKey(name);
  }

  getGeoJsonMapUrl(): string | null {
    return this.customFields.getValueOfKey("geojson_map_url");
  }

  getPathTags(): string[] | null {
    return this.pathTags ?? null;
  }

  hasPaths() {
    if (!this.paths) {
      return false;
    }
    return this.paths.nodes.length > 0;
  }

  getTranslatedProperty(prop: string): string | null {
    if (!["name", "info"].includes(prop)) return null;
    const cfValue = this.getCustomFieldValue(
      prop + "_" + i18n.resolvedLanguage,
    );
    return cfValue ?? this[prop as keyof Building]?.toString() ?? null;
  }

  isPointInsideBounds(point: LatLng): boolean {
    const { lat, lng } = point;
    const boundsCoords = [
      [this.getCorners().sw.lat, this.getCorners().sw.lng],
      [this.getCorners().nw.lat, this.getCorners().nw.lng],
      [this.getCorners().ne.lat, this.getCorners().ne.lng],
      [this.getCorners().se.lat, this.getCorners().se.lng],
      [this.getCorners().sw.lat, this.getCorners().sw.lng],
    ];

    const pt = turfPoint([lat, lng]);
    const poly = polygon([boundsCoords]);

    return booleanPointInPolygon(pt, poly);
  }

  distanceFrom(
    point: Feature<Point>,
    options?: { toCenter?: boolean },
  ): number {
    const latLng = new LatLng(
      point.geometry.coordinates[1],
      point.geometry.coordinates[0],
    );

    // Calculate distance to center of building.
    if (options?.toCenter === true) {
      const center = turfPoint([
        this.geometry.location.lng,
        this.geometry.location.lat,
      ]);
      return distance(point, center, { units: "meters" });
    }

    // If point is already inside the building, return distance 0.
    if (this.isPointInsideBounds(latLng)) {
      return 0;
    }

    // Calculate min distance from point to the bounds of the building.
    const boundsCoords = [
      [this.getCorners().sw.lng, this.getCorners().sw.lat],
      [this.getCorners().nw.lng, this.getCorners().nw.lat],
      [this.getCorners().ne.lng, this.getCorners().ne.lat],
      [this.getCorners().se.lng, this.getCorners().se.lat],
      [this.getCorners().sw.lng, this.getCorners().sw.lat],
    ];
    const poly = polygon([boundsCoords]);
    return pointToPolygonDistance(point, poly, { units: "meters" });
  }

  setIMDF(imdf: IMDF) {
    this.geojson = imdf;
  }

  static fromMap(data: any) {
    return new Building(
      data.id,
      data.name,
      data.address,
      data.info,
      data.pictureUrl,
      BuildingGeometry.fromMap(data.geometry),
      CustomFields.fromMap(data.customFields.cf),
      data?.floors.map((floor: any) => Floor.fromMap(floor)),
      Pois.fromMap(data?.pois?.pois),
      data?.paths,
      new Geofences(
        data?.geofences?.geofences?.map((g: Geofence) => new Geofence(g)),
      ),
      // fromMap
      data?.geojson,
      data?.pathTags,
      // fromMap
      data?.overlays,
    );
  }
}
export class Floor {
  constructor(
    public id: number,
    public level: number,
    public floorplan: {
      url: string;
      bounds: [number, number, number, number] | [number, number][];
    },
    public name: string,
    public customFields: CustomFields<string>,
  ) {
    this.floorplan.url = ensureAbsoluteCdnUrl(this.floorplan.url);
  }

  getPrintName(): string {
    const configuredName =
      this.getTranslatedProperty("long_name") ||
      this.getTranslatedProperty("name");

    if (!configuredName) {
      return t("explore.level", { levelName: this.level });
    } else {
      return configuredName;
    }
  }

  getCustomFieldValue(name: string): string | null {
    return this.customFields.getValueOfKey(name);
  }

  getTranslatedProperty(prop: string): string | null {
    if (!["name", "long_name"].includes(prop)) return null;
    const cfValue = this.getCustomFieldValue(
      prop + "_" + i18n.resolvedLanguage,
    );

    if (prop === "name") {
      return (
        cfValue ??
        (this[prop as keyof Floor]?.toString().trim() === ""
          ? null
          : this[prop as keyof Floor]?.toString()) ??
        null
      );
    } else
      return (
        cfValue ??
        this.getCustomFieldValue(prop) ??
        this[prop as keyof Floor]?.toString() ??
        null
      );
  }

  static fromMap(data: any) {
    return new Floor(
      data.id,
      data.level,
      data.floorplan,
      data.name,
      CustomFields.fromMap(data.customFields.cf),
    );
  }
}

//TODO: Rename to LngLat
export class LatLng {
  constructor(
    public lat: number,
    public lng: number,
  ) {
    this.lat = lat;
    this.lng = lng;
  }

  toArray() {
    return [this.lng, this.lat];
  }

  static fromMap(data: any) {
    return new LatLng(data.lat, data.lng);
  }
}

export class Corners {
  constructor(
    public nw: LatLng,
    public ne: LatLng,
    public se: LatLng,
    public sw: LatLng,
  ) {
    this.nw = nw;
    this.ne = ne;
    this.se = se;
    this.sw = sw;
  }

  /**
   * Accessor to return the TRUE top right corner of the
   * building, as 'nw' is falsely named
   */
  get topRight() {
    return this.nw;
  }

  /**
   * Accessor to return the TRUE top left corner of the
   * building, as 'ne' is falsely named
   */
  get topLeft() {
    return this.ne;
  }

  /**
   * Accessor to return the TRUE bottom right corner of the
   * building, as 'sw' is falsely named
   */
  get bottomRight() {
    return this.sw;
  }

  /**
   * Accessor to return the TRUE bottom left corner of the
   * building, as 'se' is falsely named
   */
  get bottomLeft() {
    return this.se;
  }

  toArray(): LatLng[] {
    return [this.nw, this.ne, this.se, this.sw];
  }

  static fromMap(data: any) {
    return new Corners(data.nw, data.ne, data.se, data.sw);
  }
}

export class BoundingBox {
  constructor(public latlngs: LatLng[]) {
    this.latlngs = latlngs;
  }

  getSW() {
    const [minLng, minLat] = this.latlngs.reduce(
      ([minLng, minLat], { lng, lat }) => [
        Math.min(minLng, lng),
        Math.min(minLat, lat),
      ],
      [180, 90],
    );
    return new LatLng(minLat, minLng);
  }

  getNE() {
    const [maxLng, maxLat] = this.latlngs.reduce(
      ([maxLng, maxLat], { lng, lat }) => [
        Math.max(maxLng, lng),
        Math.max(maxLat, lat),
      ],
      [-180, -90],
    );
    return new LatLng(maxLat, maxLng);
  }
}

export interface Camera {
  zoom: number | null;
  bearing: number | null;
  pitch: number | null;
  transitionDuration: number | null;
  center: LatLng | null;
}

export class Position {
  constructor(
    public building_id: number,
    public floor_id: number,
    public lat: number,
    public lng: number,
    public x?: number,
    public y?: number,
    public name?: string,
  ) {
    this.building_id = building_id;
    this.floor_id = floor_id;
    this.lat = lat;
    this.lng = lng;
    this.x = x;
    this.y = y;
    this.name = name;
  }

  toPoi({ name }: { name?: string }) {
    return new Poi(
      CUSTOM_DESTINATION_LOCATION_ID, //id
      name ?? this.name ?? "Custom destination", //name
      new Position(
        this.building_id || 0,
        this.floor_id || 0,
        this.lat || 0,
        this.lng || 0,
        this.x || 0,
        this.y || 0,
      ),
      CUSTOM_DESTINATION_LOCATION_ID, //category
      [], // categories
      "", //info
      this.building_id || 0, //buildingId
      new CustomFields([]),
    );
  }

  static fromMap(data: any) {
    return new Position(
      data.building_id,
      data.floor_id,
      data.lat,
      data.lng,
      data?.x,
      data?.y,
      data?.name,
    );
  }
}

export class Pois {
  constructor(public pois: Poi[]) {
    this.pois = pois;
  }

  push(...poi: Poi[]) {
    this.pois.push(...poi);
  }
  getPoiById(id: ID): Poi | undefined {
    return this.pois.find((p) => p.id == id);
  }

  getPoisByIds(ids: ID[]): Pois {
    return new Pois(this.pois.filter((p: Poi) => ids.includes(p.id)));
  }

  filterByIds(ids: ID[]): Pois {
    const newPois = this.pois.filter((p) => !ids.includes(p.id));
    return new Pois(newPois);
  }

  filterByFloorId(floorId: ID): Pois {
    const newPois = this.pois.filter((p) => p.position.floor_id == floorId);
    return new Pois(newPois);
  }

  map<T>(fn: (p: Poi) => T) {
    return this.pois.map((p) => {
      return fn(p);
    });
  }

  filterByTranslatedPropContainingText(
    prop: string,
    filterText: string,
    matchCase = true,
  ): Pois {
    const newPois = this.pois.filter((p) =>
      p.translatedPropertyContainsText(prop, filterText, matchCase),
    );
    return new Pois(newPois);
  }

  filterByCategory(categories: PoiCategories | null): Pois {
    if (categories == null) return new Pois(this.pois);

    const newPois = this.pois.filter((p) =>
      p.categoryIsOneOf(categories.toFlatArray()),
    );
    return new Pois(newPois);
  }

  filterByCategoryIds(categoriesIds: PoiCategory["id"][] | null): Pois {
    if (categoriesIds == null || categoriesIds.length === 0)
      return new Pois([]);

    const newPois = this.pois.filter((p) => p.categoryIdIsOneOf(categoriesIds));
    return new Pois(newPois);
  }

  filter(fn: (p: Poi) => boolean) {
    const newPois = this.pois.filter((p) => {
      return fn(p);
    });
    return new Pois(newPois);
  }

  forEach(fn: (p: Poi) => void) {
    this.pois.forEach(fn);
  }

  sortByTranslatedProp(prop: string): Pois {
    const newPois = this.pois.sort((a, b) => {
      return a.compareByTranslatedProperty(prop, b);
    });
    return new Pois(newPois);
  }

  filterByMinDistanceToPoi(targetPoi: Poi, minDistance: number): Pois {
    return new Pois(
      this.toArray().filter(
        (p: Poi) => p.distanceToPoi(targetPoi) > minDistance,
      ),
    );
  }

  sortByPriority({
    sortById,
    customTopLevelPois,
  }: {
    sortById: boolean;
    customTopLevelPois?: Poi["id"][];
  }) {
    return new Pois(
      this.pois.sort((a: Poi, b: Poi) => {
        if (a.getCustomFieldValue("top_level") !== null) {
          return -1;
        }
        if (b.getCustomFieldValue("top_level") !== null) {
          return 1;
        }
        if (customTopLevelPois?.includes(b.id)) {
          return 1;
        }
        if (customTopLevelPois?.includes(a.id)) {
          return -1;
        }
        const priorityA = parseInt(
          a.getCustomFieldValue("priority") || "11",
          10,
        );
        const priorityB = parseInt(
          b.getCustomFieldValue("priority") || "11",
          10,
        );
        if (priorityA !== priorityB) return priorityA - priorityB;

        //Keep original sort (search distance and fuzzy match score)
        return sortById ? a.id - b.id : 0;
      }),
    );
  }

  findByExternalId(externalId: string): Poi | undefined {
    return this.pois.find(
      (p) => p.getCustomFieldValue(EXTERNAL_ID_CUSTOM_FIELD) === externalId,
    );
  }

  toArray(): Poi[] {
    return this.pois;
  }

  static fromPoiOverlayFeatures(features: PoiOverlayFeature[]) {
    const pois = features.map((feature) => {
      return Poi.fromGeojson(feature);
    });
    return new Pois(pois);
  }

  toGeojson(): OverlayFeatureCollection<PoiOverlayFeature> {
    return {
      features: this.sortByTranslatedProp("name").pois.map((poi) =>
        poi.toGeojson(),
      ),
      type: "FeatureCollection",
    };
  }

  toMaplibreGeojson({
    categories,
    sortById,
    generateOverlayId = false,
  }: {
    categories: PoiCategories;
    sortById: boolean;
    generateOverlayId?: boolean;
  }): FeatureCollection {
    return {
      features: this.sortByPriority({ sortById }).pois.map((poi, sortKey) => {
        const iconData = getIcon(poi, categories);
        const selectedIconData = getSelectedIcon(poi, categories);
        const { showIcon, showLabel, hideIconAndLabel } = getPoiVisibility(
          poi,
          categories,
        );

        const isTopLevel = poi.isTopLevel();

        // Debug text
        let nameDebug = poi.getTranslatedProperty("name") ?? "";
        if (isTopLevel) {
          nameDebug += " TOP ";
        }
        const priority = poi.getCustomFieldValue("priority");
        if (priority) {
          nameDebug += ` p: ${priority}`;
        }

        const minZoom = poi.getCustomFieldValue("min_zoom");
        if (minZoom) {
          nameDebug += ` mz: ${minZoom}`;
        }
        const maxZoom = poi.getCustomFieldValue("max_zoom");
        if (maxZoom) {
          nameDebug += ` MZ: ${maxZoom}`;
        }

        return {
          geometry: {
            coordinates: [poi.position.lng, poi.position.lat],
            type: "Point",
          },
          properties: {
            floorId: poi.position.floor_id,
            hide: hideIconAndLabel,
            hideIcon: !showIcon,
            hideLabel: !showLabel,
            iconUrl: iconData.url,
            id: generateOverlayId ? createPoiOverlayFeatureId(poi.id) : poi.id,
            isTopLevel,
            maxZoom: maxZoom ?? MAX_ZOOM,
            minZoom: minZoom ?? MIN_ZOOM,
            name: poi.getTranslatedProperty("name"),
            nameDebug,
            selectedIconUrl: selectedIconData.url,
            sortKey,
            type: OverlayFeatureTypes.poi,
          },
          type: "Feature",
        };
      }),
      type: "FeatureCollection",
    };
  }

  length(): number {
    return this.pois.length;
  }

  static fromMap(data: any[]) {
    return new Pois(data?.map(Poi.fromMap));
  }
}

export class Poi {
  public surname: string;
  constructor(
    public id: number,
    public name: string,
    public position: Position,
    public categoryId: PoiCategory["id"],
    public categoryIds: PoiCategory["id"][],
    public info: string,
    public buildingId: number,
    public customFields: CustomFields<string>,
    public icon: string | null = null,
    public selectedIcon: string | null = null,
    public matchedGeofenceId: string | null = null,
    public processedInfo: Record<string, string> | null = {},
  ) {
    this.surname = customFields.getValueOfKey("surname") || "";
    this.processedInfo = processedInfo;

    this.icon = ensureAbsoluteCdnUrl(this.icon);
    this.selectedIcon = ensureAbsoluteCdnUrl(this.selectedIcon);
  }

  static CURRENT_LOCATION_ID = -1;

  getBounds() {
    return [[this.position.lng, this.position.lat]];
  }
  getCustomFieldValue(name: string): string | null {
    return this.customFields.getValueOfKey(name);
  }

  getTranslatedProperty(
    prop: string,
    lang: string | null = null,
  ): string | null {
    if (!["name", "info", "surname"].includes(prop)) return null;
    const key = prop + "_" + (lang != null ? lang : i18n.resolvedLanguage);
    const cfValue = this.getCustomFieldValue(key);
    const value = cfValue ?? this[prop as keyof Poi]?.toString() ?? null;

    if (!value) return null;
    if (containsKnownTag(value)) return value;
    return decode(value);
  }

  getTranslatedProcessedInfo(lang: string = null) {
    const key = lang != null ? lang : i18n.resolvedLanguage;
    return this.processedInfo[key];
  }

  setProcessedInfo(lang: string, value: string) {
    this.processedInfo[lang] = value;
  }

  translatedPropertyContainsText(
    prop: string,
    text: string,
    matchCase = true,
  ): boolean {
    const propVal = this.getTranslatedProperty(prop);
    if (!propVal) return false;

    return propVal.trim() === "" || matchCase
      ? propVal.trim().includes(text.trim())
      : propVal.trim().toLowerCase().includes(text.trim().toLowerCase());
  }

  categoryIsOneOf(categories: PoiCategory[]): boolean {
    return (
      categories?.find(
        (c) => c.id == this.categoryId || this.categoryIds?.includes(c.id),
      ) !== undefined
    );
  }
  categoryIdIsOneOf(categories: PoiCategory["id"][]): boolean {
    return (
      categories?.find(
        (c) => c === this.categoryId || this.categoryIds?.includes(c),
      ) !== undefined
    );
  }

  compareByTranslatedProperty(prop: string, poiB: Poi): number {
    const propValA = this.getTranslatedProperty(prop);
    const propValB = poiB.getTranslatedProperty(prop);
    if (!propValA || !propValB) return 0;
    // else if (!propValA) return -1;
    // else if (!propValB) return 1;
    return propValA.localeCompare(propValB);
  }

  isWithinZoomRange(z: number, minZoom: number, maxZoom: number): boolean {
    const minZoomValue = this.getCustomFieldValue("min_zoom");
    const maxZoomValue = this.getCustomFieldValue("max_zoom");

    const _minZoom = minZoomValue !== null ? Number(minZoomValue) : minZoom;
    const _maxZoom = maxZoomValue !== null ? Number(maxZoomValue) : maxZoom;

    return z > _minZoom && z < _maxZoom;
  }

  distanceToPoi(target: Poi): number {
    if (
      !this.position.x ||
      !this.position.y ||
      !target.position.x ||
      !target.position.y
    )
      return Infinity;

    return (
      Math.abs(this.position?.x - target.position.x) +
      Math.abs(this.position.y - target.position.y)
    );
  }

  isTopLevel() {
    return this.getCustomFieldValue("top_level") === "true";
  }

  shouldShowOnSearch(poiCategories?: PoiCategories | null): boolean {
    const showPoi = !this.getCustomFieldValue("hide")
      ?.split(",")
      .map((value) => value.trim())
      ?.includes("on_search");
    const showCategory = poiCategories
      ?.findCategoriesByIds(this.categoryIds)
      ?.every((cat) => cat.shouldShowOnSearch());

    return showPoi && !!showCategory;
  }

  shouldShowOnMap(poiCategories?: PoiCategories | null): boolean {
    const showPoi = !this.getCustomFieldValue("hide")
      ?.split(",")
      .map((value) => value.trim())
      ?.includes("on_map");
    const showCategory = poiCategories
      ?.findCategoriesByIds(this.categoryIds)
      ?.every((cat) => cat.shouldShowOnMap());

    return showPoi && !!showCategory;
  }

  mapToLocation() {
    return new Location({
      buildingId: this.buildingId,
      floorId: this.position.floor_id ?? 0,
      latitude: this.position.lat ?? 0,
      longitude: this.position.lng ?? 0,
      x: this.position.x ?? 0,
      y: this.position.y ?? 0,
    });
  }

  isEqual(poi: Poi) {
    return (
      this.id == poi.id &&
      this.name == poi.name &&
      // TODO this comparison does NOT check x and y, only lat lng, and for
      // good reason. X and Y calculated on the viewer are different than the ones
      // calculated on backend
      this.position.lat == poi.position.lat &&
      this.position.lng == poi.position.lng &&
      this.position.floor_id == poi.position.floor_id &&
      this.buildingId == poi.buildingId &&
      this.categoryId == poi.categoryId &&
      this.categoryIds == poi.categoryIds &&
      this.info == poi.info &&
      this.icon == poi.icon &&
      this.selectedIcon == poi.selectedIcon &&
      this.matchedGeofenceId == poi.matchedGeofenceId &&
      JSON.stringify(this.customFields.cf) ==
        JSON.stringify(poi.customFields.cf)
    );
  }

  static fromFindMyCar(findMyCar: SliceProps, buildingId: number) {
    return new Poi(
      FIND_MY_CAR_LOCATION_ID, //id
      t("find_my_car.name"),
      new Position(
        buildingId || 0,
        findMyCar.floorId || 0,
        findMyCar.coordinates?.lat || 0,
        findMyCar.coordinates?.lng || 0,
        0, //TODO convertirlos
        0,
      ),
      FIND_MY_CAR_LOCATION_ID, //category
      [], // categories
      "", //info
      buildingId || 0, //buildingId
      new CustomFields([]),
      "/images/car.svg",
    );
  }

  toMap() {
    const { ...object } = this;
    return object;
  }

  static fromMap(data: any) {
    return new Poi(
      data.id,
      data.name,
      Position.fromMap(data.position),
      data.categoryId,
      data.categoryIds,
      data.info,
      data.buildinId,
      CustomFields.fromMap(data?.customFields.cf),
      data?.icon,
      data?.selectedIcon,
      data?.matchedGeofenceId,
      data?.processedInfo,
    );
  }

  toGeojson(): PoiOverlayFeature {
    return {
      geometry: {
        coordinates: [this.position.lng, this.position.lat],
        type: "Point",
      },
      id: createPoiOverlayFeatureId(this.id),
      properties: {
        buildingId: this.buildingId,
        categoryId: this.categoryId,
        categoryIds: this.categoryIds,
        customFields: this.customFields.cf,
        floorId: this.position.floor_id,
        icon: this.icon,
        id: this.id,
        info: this.info,
        matchedGeofenceId: this.matchedGeofenceId,
        name: this.name,
        selectedIcon: this.selectedIcon,
        type: OverlayFeatureTypes.poi,
      },
      type: "Feature",
    } as PoiOverlayFeature;
  }

  static fromGeojson(feature: PoiOverlayFeature): Poi {
    return new Poi(
      parseInt(feature.properties.id.toString()),
      feature.properties.name,
      new Position(
        feature.properties.buildingId,
        feature.properties.floorId,
        feature.geometry.coordinates[1],
        feature.geometry.coordinates[0],
        feature.properties.x,
        feature.properties.y,
      ),
      feature.properties.categoryId,
      feature.properties.categoryIds,
      feature.properties.info,
      feature.properties.buildingId,
      new CustomFields(
        feature.properties.customFields?.filter((cf) => cf.key !== ""),
      ),
      feature.properties.icon,
      feature.properties.selectedIcon,
      feature.properties.matchedGeofenceId,
    );
  }

  toSitumCreateForm(): PoiCreateForm {
    return {
      buildingId: this.buildingId,
      categoryId: this.categoryId,
      categoryIds: this.categoryIds,
      customFields: this.customFields.toArray(),
      icon: this.icon,
      info: this.info,
      name: this.name,
      position: {
        floorId: this.position.floor_id,
        georeferences: { lat: this.position.lat, lng: this.position.lng },
      },
      selectedIcon: this.selectedIcon,
    };
  }

  toSitumUpdateForm(): PoiUpdateForm {
    return this.toSitumCreateForm();
  }
}

export class PoiCategories {
  constructor(public categories: PoiCategory[]) {}

  findCategory(
    value: unknown,
    getFieldValue: (category: PoiCategory) => unknown,
  ): PoiCategory | null {
    if (!value) return null;
    for (const category of this.categories) {
      if (getFieldValue(category) === value) return category;
      const result = category.findSubcategory(value, getFieldValue);
      if (result != null) return result;
    }
    return null;
  }

  findCategoryByCode(code: PoiCategory["code"] | null): PoiCategory | null {
    return this.findCategory(code, (category) => category.code);
  }

  findCategoryById(id: PoiCategory["id"] | null): PoiCategory | null {
    return this.findCategory(id, (category) => category.id);
  }

  findByExternalId(externalId: string): PoiCategory | undefined {
    return this.findCategory(externalId, (category) =>
      category.getCustomFieldValue(EXTERNAL_ID_CUSTOM_FIELD),
    );
  }

  findCategoriesByIds(ids: PoiCategory["id"][] | null): PoiCategories | null {
    if (!ids) return null;
    const poiCategories = ids
      .map((id) => this.findCategoryById(id))
      .filter((cat) => cat) as PoiCategory[];
    if (!poiCategories) return null;
    return new PoiCategories(poiCategories);
  }

  filterByIds(
    categoryIds: PoiCategory["id"][] | null,
    keepTopLevels = true,
  ): PoiCategories {
    if (!categoryIds) return this;
    if (keepTopLevels) {
      return new PoiCategories(
        this.categories.filter((c) => c.containsCategories(categoryIds)),
      );
    } else {
      return new PoiCategories(
        this.toFlatArray().filter((c) => categoryIds.includes(c.id)),
      );
    }
  }

  findParentCategoriesByIds(ids: PoiCategory["id"][]) {
    return new PoiCategories(
      this.filterByIds(ids).categories.filter((cat) => !cat.parentCategoryId),
    );
  }

  findSubcategoriesByIds(ids: PoiCategory["id"][]) {
    return new PoiCategories(
      this.filterByIds(ids).categories.filter((cat) => cat.parentCategoryId),
    );
  }

  filterParentCategories() {
    return new PoiCategories(
      this.categories.filter((cat) => !cat.parentCategoryId),
    );
  }

  filterSubcategories() {
    return new PoiCategories(
      this.categories.filter((cat) => cat.parentCategoryId),
    );
  }

  toFlatArray(): PoiCategory[] {
    return this.categories.flatMap((c) => c.flatten());
  }

  toArray(): PoiCategory[] {
    return this.categories;
  }

  map<T>(fn: (p: PoiCategory) => T) {
    return this.categories.map((p) => {
      return fn(p);
    });
  }

  flatMap<T>(fn: (p: PoiCategory) => T[]) {
    return this.categories.flatMap((p) => {
      return fn(p);
    });
  }

  some(fn: (p: PoiCategory) => boolean) {
    return this.categories.some((p) => {
      return fn(p);
    });
  }

  filter(fn: (p: PoiCategory) => boolean) {
    const newCats = this.categories.filter((p) => {
      return fn(p);
    });
    return new PoiCategories(newCats);
  }

  every(fn: (category: PoiCategory) => boolean) {
    return this.categories.every((cat) => fn(cat));
  }

  length(): number {
    return this.categories.length;
  }

  unshift(element: PoiCategory): number {
    return this.categories.unshift(element);
  }

  forEach(fn: (category: PoiCategory) => void) {
    return this.categories.forEach((cat) => fn(cat));
  }

  sortByName() {
    return new PoiCategories(
      this.categories.sort((a, b) => {
        if (a.code == "situm-default") {
          return -1;
        }
        if (b.code == "situm-default") {
          return 1;
        }
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }),
    );
  }

  static fromMap(data: any) {
    return new PoiCategories(
      data?.categories?.map((category: any) => PoiCategory.fromMap(category)) ??
        [],
    );
  }
}

export class PoiCategory {
  public iconUrl: string | null = "";
  public selectedIconUrl: string | null = "";
  public color: string | null;
  public subCategories: Array<PoiCategory> | null;
  public parentCategoryId: PoiCategory["id"] | null;
  public icon: IconType | null;

  constructor(
    public id: number,
    public code: string,
    public name: string,
    public customFields: CustomFields<string>,
    url?: string,
    selectedUrl?: string,
    subCategories?: Array<PoiCategory>,
    parentCategoryId?: PoiCategory["id"],
    icon?: IconType,
  ) {
    this.id = id;
    this.code = code;
    this.name = name;
    this.customFields = customFields;
    this.icon = icon || null;
    this.color = this.getCustomFieldValue("color");

    this.iconUrl = ensureAbsoluteCdnUrl(url);
    this.selectedIconUrl = ensureAbsoluteCdnUrl(selectedUrl);

    this.subCategories = subCategories || null;
    this.parentCategoryId = parentCategoryId || null;
  }

  getCustomFieldValue(name: string): string | null {
    return this.customFields.getValueOfKey(name);
  }

  getTranslatedProperty(prop: string): string | null {
    if (this.id == FAV_POI_CATEGORY_ID && prop === "name")
      return t("favorites.category_name");
    if (!["name"].includes(prop)) return null;
    const cfValue = this.getCustomFieldValue(
      `${prop}_${i18n.resolvedLanguage}`,
    );
    const value =
      cfValue ?? this[prop as keyof PoiCategory]?.toString() ?? null;

    if (!value) return null;
    if (containsKnownTag(value)) return value;
    return decode(value);
  }

  containsCategories(ids: PoiCategory["id"][]): boolean {
    // Check if the node's ID is in the given IDs
    if (ids.includes(this.id)) {
      return true;
    }

    // Recursively check the subcategories
    if (this.subCategories != null) {
      for (const s of this.subCategories) {
        if (s.containsCategories(ids)) {
          return true;
        }
      }
    }

    return false;
  }

  getFirstLevelSubcategoriesIds(): PoiCategory["id"][] | null {
    return this.subCategories?.map((s) => s.id) || null;
  }

  getAllSubcategoriesIds(): PoiCategory["id"][] | null {
    return (
      this.subCategories?.flatMap((s) => [
        s.id,
        ...(s.getAllSubcategoriesIds() || []),
      ]) || null
    );
  }

  findSubcategory(
    value: unknown,
    getFieldValue: (category: PoiCategory) => unknown,
  ): PoiCategory | null {
    if (!this.subCategories) return null;
    for (const subcategory of this.subCategories) {
      if (getFieldValue(subcategory) === value) return subcategory;
      const result = subcategory.findSubcategory(value, getFieldValue);
      if (result != null) return result;
    }
    return null;
  }

  findSubcategoryById(id: PoiCategory["id"]): PoiCategory | null {
    return this.findSubcategory(id, (category) => category.id);
  }

  findSubcategoryByExternalId(externalId: string): PoiCategory | null {
    return this.findSubcategory(externalId, (category) =>
      category.getCustomFieldValue(EXTERNAL_ID_CUSTOM_FIELD),
    );
  }

  findSubcategoryByCode(code: PoiCategory["code"]): PoiCategory | null {
    return this.findSubcategory(code, (category) => category.code);
  }

  flatten(): PoiCategory[] {
    return [this, ...(this.subCategories?.flatMap((s) => s.flatten()) || [])];
  }

  shouldShowOnSearch(): boolean {
    return !this.getCustomFieldValue("hide")
      ?.split(",")
      .map((value) => value.trim())
      .includes("on_search");
  }

  shouldShowOnMap(): boolean {
    return !this.getCustomFieldValue("hide")
      ?.split(",")
      .map((value) => value.trim())
      .includes("on_map");
  }

  static fromMap(data: any) {
    return new PoiCategory(
      data.id,
      data.code,
      data.name,
      CustomFields.fromMap(data?.customFields.cf),
      data?.iconUrl,
      data?.selectedIconUrl,

      data?.subCategories?.map((subCat: any) => PoiCategory.fromMap(subCat)),
      data?.parentCategoryId,
      data?.icon,
    );
  }

  static fromFindMyCar() {
    return new PoiCategory(
      FIND_MY_CAR_LOCATION_ID,
      "FMC",
      i18n.t("find_my_car.name"),
      new CustomFields([]),
      DEFAULT_POI_ICON_PATH,
      DEFAULT_POI_ICON_PATH,
      undefined,
      FIND_MY_CAR_LOCATION_ID,
      CarIcon as IconType,
    );
  }
}

interface ExternalFeatureProperties {
  floor_id: number;
  building_id: number;
  accuracy?: number;
  icon_size?: number;
  /** @deprecated use tooltip field instead */
  title?: string;
  tooltip?: string;
  icon_url?: string;
  persist_tooltip?: boolean;
}
export type ExternalFeature = Feature<Point, ExternalFeatureProperties>;

export type Trajectory = {
  status: "PLAY" | "PAUSE";
  speed: number;
  followUser: boolean;
  tooltip: string;
  data: TrajectoryDataPoint[];
  rangeFilter: RangeFilter;
  currentIndex: { value: number; mustReset: boolean };
  pathColor: string;
};

export type RangeFilter = {
  min: number;
  max: number;
};

export type TrajectoryDataPoint = {
  timestamp: string;
  sessionMark: number;
  floorId: number;
  userId: string;
  deviceId: string;
  lat: number;
  lng: number;
};

export class Location {
  timestamp? = 0;
  // Timestamp when this location was ingested/processed by the viewer
  ingestionTimestamp = 0;
  latitude = 0;
  longitude = 0;
  buildingId = 0;
  floorId = 0;
  x? = 0;
  y? = 0;
  accuracy = 0;
  bearing = 0;
  bearingCartesian = 0;
  isIndoor = true;
  isOutdoor = false;
  hasBearing = false;
  status: string = LocationStatusTypes.POSITIONING;
  isSimulated = false;

  constructor(params: {
    timestamp?: number | string;
    ingestionTimestamp?: number;
    latitude: number;
    longitude: number;
    buildingId?: number | null;
    floorId?: number | null;
    x?: number;
    y?: number;
    accuracy?: number;
    bearing?: number;
    bearingCartesian?: number;
    isIndoor?: boolean;
    isOutdoor?: boolean;
    hasBearing?: boolean;
    status?: string;
    isSimulated?: boolean;
  }) {
    Object.assign(this, params);

    // Set ingestionTimestamp to current time if not provided
    if (this.ingestionTimestamp == null) {
      this.ingestionTimestamp = Date.now();
    }
  }

  toPoi() {
    return new Poi(
      CURRENT_USER_LOCATION_ID, //id
      "curr position", //name
      new Position(
        this?.buildingId || 0,
        this?.floorId || 0,
        this?.latitude || 0,
        this?.longitude || 0,
        this?.x || 0,
        this?.y || 0,
      ),
      CURRENT_USER_LOCATION_ID, //category
      [], // categories
      "", //info
      this?.buildingId || 0, //buildingId
      new CustomFields([]),
    );
  }

  toPosition() {
    return new Position(
      this.buildingId,
      this.floorId,
      this.latitude,
      this.longitude,
      this.x,
      this.y,
    );
  }
}

export enum LICENSE_TYPE {
  FREE_TRIAL = "FREE_TRIAL",
  PARTNER = "PARTNER",
  ENTERPRISE = "ENTERPRISE",
  INTERNAL = "INTERNAL",
}

export class License {
  constructor(
    public id: string,
    public type: LICENSE_TYPE,
    public expirationDate: Date,
  ) {}
}

export class Organization {
  constructor(
    public id: string,
    public name: string,
    public license: License,
    public customFields: CustomFields<string>,
    public logoLoginPath?: string,
    public logoPath?: string,
    public colors?: any,
  ) {}

  getCustomFieldValue(name: string): string | null {
    return this.customFields.getValueOfKey(name);
  }
}

// TODO: change starting to calculating
export enum NavigationStatus {
  STARTING = "STARTING",
  RESTARTING = "RESTARTING",
  STARTED = "STARTED",
  PROGRESS = "PROGRESS",
  OUT_OF_ROUTE = "OUT_OF_ROUTE",
  FINISHED = "DESTINATION_REACHED",
  STOPPED = "STOPPED",
}

export type ModelConfig = {
  building_id: number;
  rail_width: number;
  wifi_config: string;
  ble_config: string;
  occ_map_config: string;
  use_outdoor_threshold: boolean;
  modification_date: string;
};

type ModelScan = {
  calibration_id: number;
  gt: { x: number; y: number }[];
};
export type ModelScans = {
  scans: ModelScan[];
};
